<template>
  <div class="form-group mb-3">
    <label v-if="label" :class="{ 'text-danger': hasError }">{{ label }}</label>
    
    <!-- Search Input Group (shown if canModify is true and no value is selected) -->
    <div class="input-group" v-if="canModify && !value">
      <input 
        type="text" 
        class="form-control shadow_inset_sm white_gradient_bg_light" 
        :class="{ 'is-invalid': hasError }"
        style="border-radius: 25px 0 0 25px !important; padding: 12px 20px !important; height: auto !important; border: 1px solid #89898995; border-right: none;"
        :placeholder="placeholder" 
        v-model="searchQuery"
        @keyup.enter="performSearch"
        :disabled="disabled"
        :maxlength="maxLength"
      />
      <div class="input-group-append">
        <button 
          class="btn btn-outline-success shadow_inset_sm" 
          type="button" 
          style="border-radius: 0 25px 25px 0 !important; font-weight: bold; padding: 12px 25px !important; height: auto !important; border: 1px solid #89898995; border-left: none;"
          @click.prevent="performSearch"
          :disabled="disabled"
        >
          <i class="fas fa-search" v-if="!loading" style="pointer-events: none;"></i>
          <i class="fas fa-spinner fa-spin" v-else style="pointer-events: none;"></i>
        </button>
      </div>
    </div>
    
    <!-- Dropdown results -->
    <div v-if="canModify && !value && searchResults.length > 0" class="border rounded shadow-sm bg-white mt-1 position-absolute" style="max-height: 200px; overflow-y: auto; z-index: 1000; width: calc(100% - 30px);">
      <div 
        v-for="opt in searchResults" 
        :key="opt[optionValue]" 
        @click="selectOption(opt)"
        class="p-2 border-bottom option-item cursor-pointer"
        style="cursor: pointer; transition: background-color 0.2s;"
        onmouseover="this.style.backgroundColor='#f8f9fa'"
        onmouseout="this.style.backgroundColor='#ffffff'"
      >
        <div class="font-weight-bold text-dark" style="font-size: 10pt;">{{ opt[optionLabel] }}</div>
        <small class="text-secondary">{{ sublabelPrefix }}{{ opt[optionSublabel] || 'N/A' }} | ID: {{ opt[optionValue] }}</small>
      </div>
    </div>
    
    <!-- No results message -->
    <div v-if="canModify && !value && searchQuery && searchResults.length === 0 && searchCompleted" class="text-muted mt-1 px-2" style="font-size: 9pt;">
      No se encontraron resultados.
    </div>
    
    <!-- Selected Card -->
    <div v-if="value" class="px-4 p-3 white_gradient_bg_light border d-flex justify-content-between align-items-center shadow_inset_sm" style="border-radius: 25px; border-color: #89898995 !important;">
      <div>
        <span class="font-weight-bold text-success" style="font-size: 11pt;">
          {{ selectedObj ? selectedObj[optionLabel] : value }}
        </span>
        <small class="text-secondary d-block" style="font-size: 9pt;" v-if="selectedObj && selectedObj[optionSublabel]">
          {{ sublabelPrefix }}{{ selectedObj[optionSublabel] }}
        </small>
      </div>
      
      <!-- Actions Dropdown -->
      <div class="dropdown" v-if="canModify && showActions">
        <button 
          class="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center" 
          type="button" 
          id="dynamicSelectActions"
          data-toggle="dropdown" 
          aria-haspopup="true" 
          aria-expanded="false"
          style="border-radius: 50%; width: 32px; height: 32px; padding: 0;"
        >
          <i class="fas fa-ellipsis-v"></i>
        </button>
        <div class="dropdown-menu dropdown-menu-right shadow border-0" aria-labelledby="dynamicSelectActions" style="border-radius: 12px; padding: 6px 0;">
          <slot name="actions"></slot>
          <button 
            class="dropdown-item d-flex align-items-center text-danger py-2" 
            type="button"
            @click="clearSelection"
            :disabled="disabled || !canModify"
          >
            <i class="fas fa-trash-alt mr-2" style="width: 16px;"></i> Quitar
          </button>
        </div>
      </div>
    </div>
    
    <small v-if="hasError" class="form-text text-danger">
      {{ errorMessage }}
    </small>
  </div>
</template>

<script>
/**
 * /------------------------------------------------/
 * COMPONENTE: DynamicSelectProvider 
 * /------------------------------------------------/
 * 
 * Este Componente es un input de tipo Autocomplete y Búsqueda Dinámica
 * que soporta consultas al API usando un buscador y una visualización
 * de tarjeta (Card) para el valor seleccionado.
 * 
 * --- INSTRUCCIONES DE USO POR PROP ---
 * 
 * @prop {String} label
 *       Uso: `:label="'Persona Solicitante'"`
 *       Efecto: Texto informativo arriba del input/tarjeta.
 * 
 * @prop {String|Number} value
 *       Uso: `v-model="current.solicitado_por"`
 *       Efecto: ID seleccionado de la opción.
 * 
 * @prop {Object} selectedObj
 *       Uso: `:selected-obj="current.solicitado_por_obj"`
 *       Efecto: Objeto completo seleccionado para mostrar sus datos en la tarjeta.
 * 
 * @prop {String} searchUrl
 *       Uso: `:search-url="'cat/lista-precio-cupon/'"`
 *       Efecto: Endpoint al cual hacer la consulta GET de búsqueda.
 * 
 * @prop {String} optionLabel
 *       Uso: `:option-label="'nombre'"`
 *       Efecto: Propiedad del objeto a mostrar como título principal.
 * 
 * @prop {String} optionSublabel
 *       Uso: `:option-sublabel="'identificacion'"`
 *       Efecto: Propiedad del objeto a mostrar como texto secundario.
 * 
 * @prop {String} optionValue
 *       Uso: `:option-value="'id'"`
 *       Efecto: Propiedad del objeto a usar como identificador único.
 * 
 * @prop {Function} filterResults
 *       Uso: `:filter-results="opt => opt.identificacion && opt.identificacion.length <= 13"`
 *       Efecto: Función callback para filtrar la lista de resultados en el cliente.
 * 
 * @prop {Boolean} [canModify=true]
 *       Uso: `:can-modify="puedeModificar"`
 *       Efecto: (Opcional, default: true) Controla si se permite buscar, modificar o quitar la selección.
 * 
 * @prop {Boolean} [showActions=true]
 *       Uso: `:show-actions="puedeModificar"`
 *       Efecto: (Opcional, default: true) Controla si se visualiza el botón de menú de los tres puntos.
 */
import DynamicService from "@/services/commons/DynamicService";
import Notifications from "@/notifications/notifications";

export default {
  name: "DynamicSelectProvider",
  props: {
    // Label above the control
    label: {
      type: String,
      default: ""
    },
    // The selected ID (v-model)
    value: {
      type: [String, Number],
      default: null
    },
    // The selected full object (for rendering the selected card)
    selectedObj: {
      type: Object,
      default: null
    },
    placeholder: {
      type: String,
      default: "Buscar..."
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // Custom permission rule (e.g., canModify)
    canModify: {
      type: Boolean,
      default: true
    },
    // Control if the three-dots actions menu is shown (optional)
    showActions: {
      type: Boolean,
      default: true
    },
    // Endpoint URL for search
    searchUrl: {
      type: String,
      required: true
    },
    // Search query parameter key
    searchParam: {
      type: String,
      default: "filter[search]"
    },
    // Key for main option label (name)
    optionLabel: {
      type: String,
      default: "nombre"
    },
    // Key for option sublabel
    optionSublabel: {
      type: String,
      default: "identificacion"
    },
    // Sublabel prefix (e.g. 'DPI: ')
    sublabelPrefix: {
      type: String,
      default: "DPI: "
    },
    // Key for option value
    optionValue: {
      type: String,
      default: "id"
    },
    // Max length of search query
    maxLength: {
      type: [Number, String],
      default: 13
    },
    // Optional filter function to run on results array
    filterResults: {
      type: Function,
      default: null
    },
    // Error state props
    hasError: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      searchQuery: "",
      loading: false,
      searchCompleted: false,
      searchResults: []
    };
  },
  watch: {
    searchQuery(newVal) {
      if (!newVal || newVal.trim() === "") {
        this.searchResults = [];
        this.searchCompleted = false;
      }
    }
  },
  methods: {
    performSearch() {
      const query = this.searchQuery ? this.searchQuery.trim() : "";
      if (!query) return;

      if (query.length > parseInt(this.maxLength)) {
        Notifications.alert.error(
          "Validación",
          `La búsqueda no puede exceder los ${this.maxLength} caracteres.`
        );
        return;
      }

      this.loading = true;
      this.searchCompleted = false;
      this.searchResults = [];

      const params = {};
      params[this.searchParam] = query;

      const parts = this.searchUrl.split('/').filter(p => p);
      const modulo = parts[0];
      const path = parts.slice(1).join('/');

      DynamicService.fetch(modulo, path, params)
        .then(res => {
          let list = res.data && res.data.results ? res.data.results : (res.data || []);
          if (this.filterResults) {
            list = list.filter(this.filterResults);
          }
          this.searchResults = list;
          this.searchCompleted = true;
        })
        .catch(err => {
          console.error("DynamicSelectProvider search error:", err);
          Notifications.alert.error(
            "Error",
            "Ocurrió un error al realizar la búsqueda."
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    selectOption(opt) {
      this.$emit("input", opt[this.optionValue]);
      this.$emit("select", opt);
      this.searchResults = [];
      this.searchQuery = "";
      this.searchCompleted = false;
    },
    clearSelection() {
      this.$emit("input", null);
      this.$emit("select", null);
      this.$emit("clear");
    }
  }
};
</script>
